import type { RegularHours } from '@/types/itinerary';
import type { Trip, TripPlace } from '@/types/trip';

/** Total days in the trip, inclusive of start and end. Minimum 1. */
export const tripDayCount = (trip: Trip): number => {
  const start = new Date(`${trip.startDate}T00:00:00`);
  const end = new Date(`${trip.endDate}T00:00:00`);
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1);
};

/** ISO date string (YYYY-MM-DD) for the given 1-indexed day number */
export const dateForDay = (trip: Trip, day: number): string => {
  const start = new Date(`${trip.startDate}T00:00:00`);
  start.setDate(start.getDate() + day - 1);
  return start.toISOString().slice(0, 10);
};

/** Day-of-week (1=Mon…7=Sun) for an ISO date */
export const dayOfWeek = (isoDate: string): number => {
  const jsDay = new Date(`${isoDate}T00:00:00`).getDay(); // 0=Sun…6=Sat
  return jsDay === 0 ? 7 : jsDay;
};

/** Find the RegularHours entry for the given date's day of week */
export const hoursForDate = (
  regular: Array<RegularHours> | undefined,
  isoDate: string,
): RegularHours | null => {
  if (!regular || regular.length === 0) return null;
  const dow = dayOfWeek(isoDate);
  return regular.find((h) => h.day === dow) ?? null;
};

/** Format an ISO date as "Mon, Jun 1" */
export const formatDayLabel = (isoDate: string): string =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

/** Format a date range as "Mon, Jun 1 – Thu, Jun 5" */
export const formatDateRange = (startDate: string, endDate: string): string => {
  const fmtOpts: Intl.DateTimeFormatOptions = { weekday: 'short', month: 'short', day: 'numeric' };
  const startStr = new Date(`${startDate}T00:00:00`).toLocaleDateString('en-US', fmtOpts);
  const endStr = new Date(`${endDate}T00:00:00`).toLocaleDateString('en-US', fmtOpts);
  return `${startStr} – ${endStr}`;
};

/** "4 days (2 full)" — full days omit first+last (travel days). Only shows parenthetical when fullDays > 0. */
export const formatDayCount = (trip: Trip): string => {
  const total = tripDayCount(trip);
  const full = Math.max(0, total - 2);
  const dayWord = total === 1 ? 'day' : 'days';
  if (full === 0) return `${total} ${dayWord}`;
  return `${total} ${dayWord} (${full} full)`;
};

/** Format "HH:MM" → "9:00am" */
export const formatScheduledTime = (time: string): string => {
  const parts = time.split(':');
  const h = Number(parts[0] ?? '0');
  const m = Number(parts[1] ?? '0');
  const suffix = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return m === 0 ? `${h12}${suffix}` : `${h12}:${String(m).padStart(2, '0')}${suffix}`;
};

/** Format "HHMM" (RegularHours format) → "9am". Handles "2400" (midnight close) as "midnight". */
export const formatRegularTime = (t: string): string => {
  if (t === '2400') return 'midnight';
  const h = Number(t.slice(0, 2));
  const m = t.slice(2);
  const suffix = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return m === '00' ? `${h12}${suffix}` : `${h12}:${m}${suffix}`;
};

/** "90" → "1h 30m"; "60" → "1h"; "45" → "45m"; null/0 → null. */
export const formatDurationMinutes = (minutes: number | null | undefined): string | null => {
  if (!minutes || minutes <= 0) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

/** Parse "HHMM" → minutes-since-midnight. Returns null if the string is
 * malformed. "2400" maps to 1440 (end-of-day sentinel). */
const hhmmToMinutes = (t: string): number | null => {
  if (t.length !== 4) return null;
  const h = Number(t.slice(0, 2));
  const m = Number(t.slice(2));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

export type OpenStatus = 'open' | 'closing-soon' | 'closed';

/** Compute today's open/closing-soon/closed status for a place given its hours
 * for *today's* date and the current wall-clock time. Returns null when we
 * don't have today's hours (caller falls back to "Hours unavailable"). The
 * "closing soon" window is 60 minutes — feels right for trip-planning where a
 * one-hour heads-up is the useful threshold. */
export const computeOpenStatus = (
  todayHours: RegularHours | null,
  nowMinutes: number,
  closingSoonWindowMinutes = 60,
): OpenStatus | null => {
  if (!todayHours) return null;
  const openM = hhmmToMinutes(todayHours.open);
  const closeM = hhmmToMinutes(todayHours.close);
  if (openM == null || closeM == null) return null;
  // 24-hour open
  if (todayHours.open === '0000' && todayHours.close === '2400') return 'open';
  // Across-midnight close (e.g. open 17:00, close 02:00 next day) — treat as
  // open if now is past open OR before close. Closing-soon only applies if
  // close is later today (not the next-day branch); otherwise we'd report it
  // every evening even when the place just opened.
  if (closeM <= openM) {
    if (nowMinutes >= openM) return 'open';
    if (nowMinutes < closeM) return 'open';
    return 'closed';
  }
  if (nowMinutes < openM || nowMinutes >= closeM) return 'closed';
  return closeM - nowMinutes <= closingSoonWindowMinutes ? 'closing-soon' : 'open';
};

/** Find the lodging TripPlace for a given 1-indexed day, if any (handles multi-night ranges). */
export const lodgingForDay = (places: Array<TripPlace>, day: number): TripPlace | null =>
  places.find(
    (p) =>
      p.placeType === 'lodging' &&
      p.scheduledDay != null &&
      day >= p.scheduledDay &&
      day <= (p.scheduledDayEnd ?? p.scheduledDay),
  ) ?? null;

/** For a lodging place on a specific day, return whether it's a check-in /
 * check-out / middle / not-applicable for the timeline. A single-day stay
 * with both times defaults to `check-in` so it doesn't double up on the
 * timeline (the check-out is implicit). */
export type LodgingBoundary = 'check-in' | 'check-out' | 'middle' | null;
export const lodgingBoundaryForDay = (tripPlace: TripPlace, day: number): LodgingBoundary => {
  if (tripPlace.placeType !== 'lodging' || tripPlace.scheduledDay == null) return null;
  const end = tripPlace.scheduledDayEnd ?? tripPlace.scheduledDay;
  if (day < tripPlace.scheduledDay || day > end) return null;
  if (day === tripPlace.scheduledDay) return 'check-in';
  if (day === end) return 'check-out';
  return 'middle';
};

/** Effective time used to slot a lodging entry into the day's timeline.
 * Returns null when the lodging shouldn't appear in the timeline (middle day,
 * or boundary day without a configured time). */
export const lodgingTimelineTime = (tripPlace: TripPlace, day: number): string | null => {
  const boundary = lodgingBoundaryForDay(tripPlace, day);
  if (boundary === 'check-in') return tripPlace.checkInTime ?? null;
  if (boundary === 'check-out') return tripPlace.checkOutTime ?? null;
  return null;
};

export interface TripDayHours {
  day: number;
  isoDate: string;
  hours: RegularHours | null;
}

/** Returns one entry per trip day, each with the place's hours (or null if closed/unavailable).
 * Takes startDate/endDate (not the full Trip) so callers can pass a tiny stable
 * object — important for downstream React.memo to work. */
export const getTripDayHours = (
  tripDates: { startDate: string; endDate: string },
  regular: Array<RegularHours>,
): Array<TripDayHours> => {
  const start = new Date(`${tripDates.startDate}T00:00:00`);
  const end = new Date(`${tripDates.endDate}T00:00:00`);
  const count = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1);
  return Array.from({ length: count }, (_, i) => {
    const day = i + 1;
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const isoDate = d.toISOString().slice(0, 10);
    return { day, isoDate, hours: hoursForDate(regular, isoDate) };
  });
};

/** Derived trip state for badge display: where the user is in their
 * planning journey. Mirrors the mode-name family. */
export type TripState = 'Scouting' | 'Curating' | 'Ready to Travel';

export const getTripState = (trip: Trip): TripState => {
  if (trip.places.length === 0) return 'Scouting';
  const scheduledCount = trip.places.filter((p) => p.scheduledDay != null).length;
  if (scheduledCount === 0) return 'Scouting';
  if (scheduledCount === trip.places.length) return 'Ready to Travel';
  return 'Curating';
};
